import React, { useEffect, useRef, useState } from 'react';

function AnimatedLogo({ size = 200 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const phaseRef = useRef(0);
  const [isHovered, setIsHovered] = useState(false);
  const speedMultiplierRef = useRef(1);
  const targetSpeedRef = useRef(1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    const scale = size / 2.4; // Scale to fit within canvas

    const draw = (phase) => {
      // Clear canvas
      ctx.clearRect(0, 0, size, size);

      // Set line style
      ctx.strokeStyle = 'rgba(160, 196, 255, 0.8)';
      ctx.lineWidth = 2;

      // Draw each harmonic circle
      for (let n of [3, 4, 5, 6, 7, 8, 9]) {
        ctx.beginPath();

        // Generate points along the circle
        const numPoints = 200;
        for (let i = 0; i <= numPoints; i++) {
          const theta = (i / numPoints) * 2 * Math.PI;

          // r = 1 + 0.1 * cos(n*theta + phase)
          const r = 1 + 0.1 * Math.cos(n * theta + phase);

          // Convert polar to cartesian with rotation
          const x = r * Math.cos(theta + phase / 6);
          const y = r * Math.sin(theta + phase / 6);

          // Scale and translate to canvas coordinates
          const canvasX = centerX + x * scale;
          const canvasY = centerY + y * scale;

          if (i === 0) {
            ctx.moveTo(canvasX, canvasY);
          } else {
            ctx.lineTo(canvasX, canvasY);
          }
        }

        ctx.closePath();
        ctx.stroke();
      }
    };

    const animate = () => {
      // Smoothly interpolate speed multiplier
      speedMultiplierRef.current += (targetSpeedRef.current - speedMultiplierRef.current) * 0.05;

      // Update phase (slowed down by factor of 10, multiplied by speed)
      phaseRef.current += ((12 * Math.PI) / 600) * speedMultiplierRef.current;
      if (phaseRef.current >= 12 * Math.PI) {
        phaseRef.current = 0;
      }

      draw(phaseRef.current);
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animate();

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size]);

  // Handle hover effects
  useEffect(() => {
    if (isHovered) {
      targetSpeedRef.current = 2;
    } else {
      targetSpeedRef.current = 1;
    }
  }, [isHovered]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'block',
        margin: '1rem auto -1rem auto',
        cursor: 'pointer',
        transition: 'transform 0.3s ease',
        transform: isHovered ? 'scale(1.05)' : 'scale(1)'
      }}
    />
  );
}

export default AnimatedLogo;
